/*
 * Copyright (c) 2022 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormErrorComponent } from './errors/components/form-error/form-error.component';
import { ErrorService } from './errors/services/error.service';
import { ErrorsHelper } from './errors/errors.helper';
import { StringLimitPipe } from './pipes/stringLimit.pipe';
import { PersonService } from './person-shared/person.service';
import { PersonSharedComponent } from './person-shared/person-shared/person-shared.component';
import { FirmSharedComponent } from './firm-shared/firm-shared/firm-shared.component';
import { FirmService } from './firm-shared/firm.service';
import { DateRangeComponent } from './date-range/date-range.component';
import { ResourceAvailabilityComponent } from './resource-availability/resource-availability/resource-availability.component';
import { PbarCellComponent } from './resource-availability/custom-progress-bar/components/pbar-cell/pbar-cell.component';
import { CustomProgressBarComponent } from './resource-availability/custom-progress-bar/custom-progress-bar.component';
import { ResourceAvailabilityService } from './resource-availability/resource-availability.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpCallsCounterInterceptor } from './loading-bar/http-calls-counter-interceptor';
import { HttpCallsCountService } from './loading-bar/http-calls-counter.service';
import { LoadingBarComponent } from './loading-bar/loading-bar/loading-bar.component';
import { ResourceLegendComponent } from './resource-availability/resource-legend/resource-legend.component';
import { TimeIntervalComponent } from './time-interval/time-interval.component';
import { EndpointService } from './utils/endpoint.service';
import { ReferencedPlaceService } from './places/referenced-place.service';
import { SafePipe } from './pipes/safe.pipe';
import { NumberIntervalComponent } from './number-interval/number-interval.component';
import * as langs from '../i18n/i18n';
import { StringToDatePipe } from './pipes/string-to-date.pipe';
import { DisableAutocompleteDirective } from './directives/disable-autocomplete.directive';
import { TableComponent } from './table/table.component';
import { DynamicPipe } from './table/dynamicPipe/dynamic.pipe';
import { DefaultActionsComponent } from './table/components/default-actions/default-actions.component';
import { TableTemplateDirective } from './table/utils/table-template.directive';
import { TableModule } from 'primeng/table';
import { IrianSortableColumnDirective } from './table/directives/irian-sortable-column.directive';
import { IrianReorderableColumnDirective } from './table/directives/irian-reorderable-columns';
import { StringToSelectItemsPipe } from './pipes/string-to-select-items.pipe';
import { SideSheetContainerComponent } from './side-sheet/side-sheet-container/side-sheet-container.component';
import { SideSheetDirective } from './side-sheet/side-sheet.directive';
import { TutorialResourceService } from './tutorial-service/tutorial-resource.service';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BsAutocompleteComponent, BsItemDirective } from '@well/iriro-frontend-libs/bs-component-store';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { Popover } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { FillWindowHeightDirective } from './fill-window-height/fill-window-height.directive';
import { NoCommaPipe } from './pipes/no-comma.pipe';
import { CustomArrayItemsFieldEqualToValidator } from './errors/validators/custom-array-items-field-equal-to.validator';
import { CustomDateRangeValidator } from './errors/validators/custom-date-range.validator';
import { MultipleAutoCompleteButtonComponent } from './multiple-auto-complete-button/multiple-auto-complete-button.component';
import { MultipleAutoCompletePanelComponent } from './multiple-auto-complete-panel/multiple-auto-complete-panel.component';
import { CheckboxModule } from 'primeng/checkbox';
import { PaginatorModule } from 'primeng/paginator';
import { FormControlErrorsComponent } from './form/form-control-errors/form-control-errors.component';
import { FormFieldErrorComponent } from './form/form-field-error/form-field-error.component';
import { RootFormDirective } from './form/root-form-directive/root-form.directive';
import { TranslateSelectItemsPipe } from './pipes/translate-select-items-pipe/translate-select-items.pipe';
import { FormatToFixedPipe } from './pipes/format-to-fixed.pipe';
import { TreeLinesComponent } from './tree-table/tree-lines/tree-lines.component';
import { UiBlockerOverlayComponent } from './ui-blocker-for-save-request/ui-blocker-overlay-component/ui-blocker-overlay.component';
import { PreventKeyPressDirective } from './ui-blocker-for-save-request/ui-blocker-overlay-component/prevent-key-press.directive';
import { PreventScrollDirective } from './ui-blocker-for-save-request/ui-blocker-overlay-component/prevent-scroll.directive';
import { AttachmentsService } from './attachment-commons/service/attachments.service';
import { FileThumbnailComponent } from './attachment-commons/components/file-thumbnail/file-thumbnail.component';
import { CurrencyValuePipe } from './costs/currency-value.pipe';
import { FilesEditorComponent } from './attachment-commons/components/files-editor/files-editor.component';
import { FileUploadModule } from 'primeng/fileupload';
import { ConfirmationService } from 'primeng/api';
import { BankAccountDialogComponent } from './firm-shared/firm-shared/bank-account-dialog/bank-account-dialog.component';
import { RippleModule } from 'primeng/ripple';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { CustomPatternValidator } from './errors/validators/custom-pattern.validator';
import { MinimumLengthValidator } from './errors/validators/minimum-length.validator';
import { CustomRequiredWithoutZeroValidator } from './errors/validators/custom-required-without-zero.validator';
import { CustomWhitespaceValidator } from './errors/validators/custom-whitespace.validator';
import { CustomRequiredTrueValidator } from './errors/validators/custom-required-true.validator';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    MultiSelectModule,
    DatePicker,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    FormsModule,
    MessagesModule,
    MessageModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    AutoCompleteModule,
    TableModule,
    TooltipModule,
    Popover,
    CheckboxModule,
    PaginatorModule,
    FileUploadModule,
    RippleModule,
    InputGroupModule,
    InputGroupAddonModule,
    DatePicker,
    FloatLabel,
    Popover,
    BsAutocompleteComponent,
    BsItemDirective,
  ],
  exports: [
    FormErrorComponent,
    DateRangeComponent,
    CustomDateRangeValidator,
    CustomArrayItemsFieldEqualToValidator,
    CustomRequiredWithoutZeroValidator,
    CustomWhitespaceValidator,
    CustomRequiredTrueValidator,
    PersonSharedComponent,
    FirmSharedComponent,
    ResourceAvailabilityComponent,
    StringLimitPipe,
    LoadingBarComponent,
    ResourceLegendComponent,
    SafePipe,
    StringToDatePipe,
    NumberIntervalComponent,
    TimeIntervalComponent,
    DisableAutocompleteDirective,
    TableComponent,
    TableTemplateDirective,
    DefaultActionsComponent,
    DynamicPipe,
    IrianSortableColumnDirective,
    IrianReorderableColumnDirective,
    StringToSelectItemsPipe,
    SideSheetContainerComponent,
    SideSheetDirective,
    FillWindowHeightDirective,
    NoCommaPipe,
    MultipleAutoCompleteButtonComponent,
    MultipleAutoCompletePanelComponent,
    TranslateSelectItemsPipe,
    FormatToFixedPipe,
    TreeLinesComponent,
    UiBlockerOverlayComponent,
    PreventKeyPressDirective,
    PreventScrollDirective,
    FileThumbnailComponent,
    CurrencyValuePipe,
    FilesEditorComponent,
    CustomPatternValidator,
    MinimumLengthValidator,
  ],
  declarations: [
    CustomPatternValidator,
    MinimumLengthValidator,
    FormErrorComponent,
    DateRangeComponent,
    CustomArrayItemsFieldEqualToValidator,
    CustomWhitespaceValidator,
    CustomRequiredWithoutZeroValidator,
    CustomRequiredTrueValidator,
    StringLimitPipe,
    PersonSharedComponent,
    FirmSharedComponent,
    ResourceAvailabilityComponent,
    CustomProgressBarComponent,
    PbarCellComponent,
    LoadingBarComponent,
    ResourceLegendComponent,
    NumberIntervalComponent,
    TimeIntervalComponent,
    SafePipe,
    StringToDatePipe,
    DisableAutocompleteDirective,
    TableComponent,
    TableTemplateDirective,
    DefaultActionsComponent,
    DynamicPipe,
    IrianSortableColumnDirective,
    IrianReorderableColumnDirective,
    StringToSelectItemsPipe,
    SideSheetContainerComponent,
    SideSheetDirective,
    FillWindowHeightDirective,
    FormControlErrorsComponent,
    FormFieldErrorComponent,
    RootFormDirective,
    NoCommaPipe,
    CustomDateRangeValidator,
    MultipleAutoCompleteButtonComponent,
    MultipleAutoCompletePanelComponent,
    TranslateSelectItemsPipe,
    FormatToFixedPipe,
    TreeLinesComponent,
    UiBlockerOverlayComponent,
    PreventKeyPressDirective,
    PreventScrollDirective,
    FileThumbnailComponent,
    CurrencyValuePipe,
    FilesEditorComponent,
    BankAccountDialogComponent,
  ],
  providers: [DatePipe, ConfirmationService, DynamicDialogRef, DialogService],
})
export class SharedComponentsModule {
  constructor(translateService: TranslateService) {
    Object.keys(langs).map((lang) => {
      translateService.setTranslation(lang, langs[lang as keyof typeof langs], true);
    });
  }

  static forRoot(): ModuleWithProviders<SharedComponentsModule> {
    return {
      ngModule: SharedComponentsModule,
      providers: [
        ErrorService,
        ResourceAvailabilityService,
        ErrorsHelper,
        PersonService,
        FirmService,
        HttpCallsCountService,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: HttpCallsCounterInterceptor,
          multi: true,
        },
        EndpointService,
        ReferencedPlaceService,
        TutorialResourceService,
        AttachmentsService,
      ],
    };
  }
}
